/**
 * What a view that reads on sight should mark read — a calculation.
 *
 * The read cursor is ONE id across the whole channel (replies included), so a view can
 * only trust its own loaded messages when they ARE the channel:
 *
 * - **The full thread** reads up to its newest loaded message, and is behind while that
 *   message is newer than the cursor.
 * - **A partial view** — a roots-only list (the office notes, whose replies live in their
 *   threads) or one reply thread — never holds the newest message when it is a reply
 *   elsewhere, so it asks the server: behind while the server counts anything unread for
 *   the viewer, and then reads the WHOLE thread (`POST /:cid/read` with no id). The count
 *   alone is not enough: a viewer with no member row yet answers `0` with a null cursor,
 *   so a never-read thread with anything on screen counts as behind too.
 */

import type { ReadCursorId } from "./unread-boundary";

export type ReadOnViewTarget =
  | { kind: "none" }
  | { kind: "upTo"; messageId: string }
  | { kind: "wholeThread" };

export type ReadOnViewInput = {
  /** Whether the loaded messages are the whole channel (not roots-only, not one thread). */
  coversWholeChannel: boolean;
  /** Newest persisted loaded message, or `null` when nothing is loaded. */
  newestMessageId: string | null;
  /** `undefined` while the cursor is still loading — never read as "read nothing". */
  lastReadMessageId: ReadCursorId;
  /** The server's unread count for the viewer; `undefined` while loading. */
  unreadCount: number | undefined;
};

const NONE: ReadOnViewTarget = { kind: "none" };

export function computeReadOnViewTarget({
  coversWholeChannel,
  newestMessageId,
  lastReadMessageId,
  unreadCount,
}: ReadOnViewInput): ReadOnViewTarget {
  if (newestMessageId == null) return NONE;

  // Time-sortable nanoids of equal length: string order is creation order.
  const isLoadedBehind =
    lastReadMessageId == null || newestMessageId > lastReadMessageId;

  if (coversWholeChannel) {
    return isLoadedBehind ? { kind: "upTo", messageId: newestMessageId } : NONE;
  }

  // A partial view decides on the server's answer, so it waits for it.
  if (lastReadMessageId === undefined || unreadCount === undefined) return NONE;
  return unreadCount > 0 || lastReadMessageId === null
    ? { kind: "wholeThread" }
    : NONE;
}
